import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, X } from "lucide-react";

const PdfViewerContext = createContext(null);

export const usePdfViewer = () => {
    const ctx = useContext(PdfViewerContext);
    if (!ctx) throw new Error("usePdfViewer must be used inside PdfViewerProvider");
    return ctx;
};

export const PdfViewerProvider = ({ children }) => {
    const [state, setState] = useState({ open: false, url: null, filename: "document.pdf" });

    const openPdf = useCallback((blob, filename) => {
        const url = URL.createObjectURL(blob);
        setState({ open: true, url, filename });
    }, []);

    const close = useCallback(() => {
        setState((s) => {
            if (s.url) URL.revokeObjectURL(s.url);
            return { open: false, url: null, filename: "document.pdf" };
        });
    }, []);

    // Cleanup on unmount
    useEffect(() => () => {
        if (state.url) URL.revokeObjectURL(state.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownload = () => {
        if (!state.url) return;
        const a = document.createElement("a");
        a.href = state.url;
        a.download = state.filename;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const openInNewTab = () => {
        if (!state.url) return;
        // Open in top-level window (escape sandboxed iframe if possible)
        try {
            window.open(state.url, "_blank", "noopener,noreferrer");
        } catch (_) {
            window.location.href = state.url;
        }
    };

    return (
        <PdfViewerContext.Provider value={{ openPdf, close }}>
            {children}
            <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
                <DialogContent
                    className="max-w-5xl w-[95vw] h-[90vh] p-0 rounded-none border-zinc-200 flex flex-col gap-0 [&>button]:hidden"
                    data-testid="pdf-viewer-dialog"
                >
                    <DialogHeader className="px-5 py-3 border-b border-zinc-200 flex flex-row items-center justify-between space-y-0">
                        <DialogTitle className="font-display text-base font-bold tracking-tight truncate">
                            {state.filename}
                        </DialogTitle>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={handleDownload}
                                size="sm"
                                className="h-9 rounded-none bg-black hover:bg-zinc-800 text-white"
                                data-testid="pdf-viewer-download"
                            >
                                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                İndir
                            </Button>
                            <Button
                                onClick={openInNewTab}
                                size="sm"
                                variant="outline"
                                className="h-9 rounded-none border-zinc-300"
                                data-testid="pdf-viewer-newtab"
                            >
                                <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Yeni Sekme
                            </Button>
                            <Button
                                onClick={close}
                                size="sm"
                                variant="outline"
                                className="h-9 w-9 p-0 rounded-none border-zinc-300"
                                data-testid="pdf-viewer-close"
                            >
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 bg-zinc-100 min-h-0">
                        {state.url && (
                            <iframe
                                title={state.filename}
                                src={state.url}
                                className="w-full h-full border-0"
                                data-testid="pdf-viewer-iframe"
                            />
                        )}
                    </div>
                    <div className="px-5 py-2 border-t border-zinc-200 bg-zinc-50 text-xs text-zinc-600">
                        İpucu: Sağ üstteki <strong>İndir</strong> butonu çalışmazsa, PDF görüntüleyicinin kendi indirme simgesini kullanabilirsiniz.
                    </div>
                </DialogContent>
            </Dialog>
        </PdfViewerContext.Provider>
    );
};
